/*------------------------------------------------------------------------------
   About      : Currently this manager deals with data being stored on
                localStorage. Eventually this should be responsible for all
                session related functions.

   Created on : Thu Nov 18 2021
   Author     : Siddharth Garg

   Copyright (c) 2021 Bidgely. All rights reserved.
------------------------------------------------------------------------------*/

import { EnhancedStore }                from "@reduxjs/toolkit";
import { CMSPilotApis }                 from "apis/cmsPilotAPI";
import { RunContextApp } 						    from "framework/RCApp"
import { setApplianceProfile }          from "store/dataReducers/applianceProfileSlice";
import { setHomeProfile }               from "store/dataReducers/homeProfileSlice";
import { selectCMSPilot, 
         setSelectedPilotRecoScore,
         upsertCMSPilot
       }                                from "store/dataReducers/cmsPilotSlice"
import { resetRecoState }               from "store/dataReducers/recommendationsSlice"
import { UserAuthInfo, 
         UserInfo
       }                                from "types/apis/sessionInterfaces"
import { GetCMSPilotData }              from "types/apis/cmsPilotInterfaces";
import { APIData }                      from "types/cms/frontendTypes";
import { SessionApis }                  from "apis/sessionAPI";
import { RbacApis }                     from "apis/rbacAPI";
import { PermissionTreeNode,
         AnnotatedFeature }             from "types/apis/rbacInterfaces";
import { resetRecoCommentsCount }       from "store/dataReducers/recoCommentsSlice";
import { ANALYTICS_EVENTS }             from "framework/analytics/AnalyticsEvents";

export class SessionManager {

  private isLoggedIn        : boolean = false;
  private cmsAccessToken    : string = ''
  private user             !: UserInfo
  private store            !: EnhancedStore
  
	constructor(private rc : RunContextApp) {}

  setStore(store : EnhancedStore) {
    this.store = store
  }

	getUserLocale() {
		return ''
	}

  async init(userInfo ?: UserAuthInfo) : Promise<string | undefined> {

    if (userInfo) {
      this.cmsAccessToken = userInfo.jwt
      this.user = userInfo.user

      localStorage.setItem("accessToken", this.cmsAccessToken)
      localStorage.setItem("userInfo", JSON.stringify(this.user))

      return this.buildSession()

    } else { // Try to load from localStorage
      const cmsAccessToken = localStorage.getItem("accessToken")
      const userInfo = localStorage.getItem("userInfo")

      if (!cmsAccessToken || !userInfo) {
        return "Invalid session"
      }

      this.cmsAccessToken = cmsAccessToken
      try {
        this.user = JSON.parse(userInfo)
      } catch (e) {
        // If localStorage got corrupted (common in dev), avoid crashing the app.
        localStorage.removeItem("accessToken")
        localStorage.removeItem("userInfo")
        return "Invalid session"
      }

      return this.buildSession()
    }
  }

  private async buildSession() : Promise<string | undefined> {

    this.rc.cmsApiClient.init(this.cmsAccessToken)
      
    const userRoleInfoError = await this.requestUserRoleInfo()
    if (userRoleInfoError) {
      return userRoleInfoError
    }

    await this.requestBffFeatures()

    this.isLoggedIn = true
  }

  private async requestUserRoleInfo() : Promise<string | undefined> {

    const userRoleInfoResp = await SessionApis.getUserRoleInfo(this.rc)
    if (userRoleInfoResp.error) {
      return userRoleInfoResp.error.message
    } 

    const userRoleInfo = userRoleInfoResp.data!
    this.rc.permissionsManager.setUserRoleInfo(userRoleInfo)
    this.rc.accessInfo.setUserRoleInfo(userRoleInfo)

    // Identify the user for GA4 segmentation. user_id is a stable, non-PII id
    // (documentId / numeric id) — never the raw email.
    const roleName = userRoleInfo.role?.name
    this.rc.analytics.setUserProperties({
      user_id   : this.user?.documentId || (this.user?.id != null ? String(this.user.id) : undefined),
      user_role : roleName,
      user_tier : (roleName === 'SUPER_ADMIN' || roleName === 'FULL_ACCESS') ? 'admin' : 'internal',
    })

    return
  }

  /**
   * proxypulse's own RBAC is a second, disconnected authority from Strapi's
   * (see PermissionCatalog.ts's BFF_FEATURES banner) — fetched separately via
   * the same user id, since proxypulse's DAAuthUser.id is the Strapi user id.
   * Best-effort: a failure here must not block login, it just leaves
   * BFF_FEATURES-gated UI deny-by-default (empty granted set).
   */
  private async requestBffFeatures() : Promise<void> {

    const userId = this.user?.id
    if (userId == null) return

    try {
      const treeResp = await RbacApis.getUserPermissionTree(this.rc, userId)
      if (!treeResp.success || !treeResp.data) return

      const endpoints : string[] = []

      const collectFeature = (f : AnnotatedFeature) => {
        if (f.grant_state === 'granted') endpoints.push(f.endpoint)
      }
      const collectGroup = (node : PermissionTreeNode) => {
        node.features.forEach(collectFeature)
        node.children.forEach(collectGroup)
      }

      treeResp.data.groups.forEach(collectGroup)
      treeResp.data.uncategorized_features.forEach(collectFeature)

      this.rc.accessInfo.setBffFeatures(endpoints)
      this.rc.accessInfo.setSuperAdmin(treeResp.data.is_super_admin)
    } catch (e) {
      this.rc.error(this.rc.getName(SessionManager),
        'Error >> fetching proxypulse BFF permission tree', e)
    }
  }

  getUserInfo() {
    return this.user
  }

  getCMSAccessToken() {
    return this.cmsAccessToken
  }

  isUserLoggedIn() {
    return this.isLoggedIn
  }

  logoutUser() {
    this.rc.analytics.trackEvent(ANALYTICS_EVENTS.AUTH.LOGOUT_COMPLETE);
    localStorage.removeItem("accessToken");
    localStorage.removeItem("userInfo");
    window.location.reload();
  }

  async selectCMSPilot(utility : APIData<"api::utility.utility">) {
   
    const pilotDataResp = await CMSPilotApis.getPilotData(this.rc, utility.documentId)
    if (pilotDataResp.error) {
      return pilotDataResp.error
    }
    this.populateCMSPilotData(pilotDataResp.data!)
  }

  async selectCMSPilotByPilotId(pilotId : string) : Promise<string | undefined> {

    const pilotDataResp = await CMSPilotApis.getPilotDataByPilotId(this.rc, pilotId)
    if (pilotDataResp.error) {
      return pilotDataResp.error
    }
    this.populateCMSPilotData(pilotDataResp.data!)
  }

  private async populateCMSPilotData(pilotData : GetCMSPilotData.retval) {

    const { utility, homeProfile, applianceProfile, recoScore } = pilotData

    // Keep active_project super property in sync — attached to every event.
    this.rc.analytics.setSuperProperty('active_project', utility.documentId)

    this.store.dispatch(upsertCMSPilot(utility))
    this.store.dispatch(selectCMSPilot({docId : utility.documentId}))
    this.store.dispatch(setHomeProfile(homeProfile))
    this.store.dispatch(setApplianceProfile(applianceProfile))
    this.store.dispatch(setSelectedPilotRecoScore(recoScore))
    this.store.dispatch(resetRecoState())
    this.store.dispatch(resetRecoCommentsCount())
  }

}